import { dynamicImportWithRetry } from '@fatso83/retry-dynamic-import'
import { type ComponentType, type LazyExoticComponent, lazy } from 'react'

/**
 * Amplify Hosting deploys are atomic: every push deletes the previous
 * build's content-hashed chunks, so tabs opened before a deploy get a
 * hard 404 on the next lazy route they visit. Retrying the same URL
 * (what @fatso83/retry-dynamic-import does) can never succeed in that
 * case — only a full document reload picks up the new index.html and
 * chunk map. The sessionStorage guard keeps a genuinely broken chunk
 * (e.g. a network outage) from causing a reload loop; past the guard
 * the error is rethrown for the router error boundary to display.
 */

const RELOAD_GUARD_KEY = 'chunk-load-error-reload-at'
const RELOAD_GUARD_WINDOW_MS = 60_000
const RELOAD_TAKEOVER_TIMEOUT_MS = 5_000

function isChunkLoadError(error: unknown): boolean {
	if (!(error instanceof Error)) return false
	const text = `${error.name} ${error.message}`.toLowerCase()
	return (
		text.includes('chunkloaderror') ||
		text.includes('loading chunk') ||
		text.includes('loading css chunk') ||
		text.includes('dynamically imported module') ||
		text.includes('importing a module script failed')
	)
}

function recentlyAutoReloaded(): boolean {
	try {
		const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY))
		return last > 0 && Date.now() - last < RELOAD_GUARD_WINDOW_MS
	} catch {
		// sessionStorage unavailable → no loop protection, so never reload
		return true
	}
}

function markAutoReloaded() {
	try {
		sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()))
	} catch {
		// ignore — guard already blocked the reload path above
	}
}

export async function loadWithReloadFallback<T>(
	importer: () => Promise<T>
): Promise<T> {
	try {
		return await dynamicImportWithRetry(importer)
	} catch (error) {
		if (
			isChunkLoadError(error) &&
			navigator.onLine !== false &&
			!recentlyAutoReloaded()
		) {
			markAutoReloaded()
			window.location.reload()
			// Keep the Suspense fallback (spinner) up while the reload
			// takes over; if navigation somehow never starts, surface the
			// error to the boundary instead of spinning forever
			return new Promise<T>((_, reject) => {
				setTimeout(() => reject(error), RELOAD_TAKEOVER_TIMEOUT_MS)
			})
		}
		throw error
	}
}

export default function reactLazyWithRetry(
	importer: () => Promise<any>
): LazyExoticComponent<ComponentType<any>> {
	return lazy(() => loadWithReloadFallback(importer))
}
